<template>
  <div class="pathways-page">
    <!-- Hero Section -->
    <section class="hero-section position-relative d-flex flex-column justify-end">
      <div class="hero-overlay w-100 h-100 d-flex flex-column justify-end">
        <v-container class="px-4 px-lg-0">
          <div class="hero-content">
            <h1 class="hero-title text-white font-weight-bold mb-3">Degree pathways</h1>
            <div class="hero-subtitle d-none d-sm-block">You're closer to your degree than ever</div>
          </div>
        </v-container>
      </div>
    </section>

    <!-- Follow Your Passions Section -->
    <section class="bg-white py-8 py-lg-12 px-4">
      <v-container class="pa-0">
        <v-row no-gutters>
          <v-col cols="12" lg="8" class="pe-lg-8">
            <h2 class="section-title font-weight-bold mb-4 mt-0">Follow your passions toward a degree</h2>
            <p class="body-text mb-4">
              When you choose a pathway, you can access Arizona State University courses at low personal or financial
              risk. You'll try out courses aligned with your future degree of interest without having to be admitted to
              ASU or pay upfront.
            </p>
            <p class="body-text mb-6">
              When you meet the admission requirements for your pathway and submit an ASU application (including
              <a href="https://admission.asu.edu/apply" class="link-maroon" target="_blank">required materials</a>)
              you'll be eligible for admission, regardless of your past academic experiences. Once you have added all
              your courses to your academic transcript, you'll have a leg up when transitioning into your degree
              program.
            </p>
          </v-col>
          <v-col cols="12" lg="4" class="d-none d-lg-flex justify-center align-center">
            <img :src="passionImage" alt="A man deciding a pathway with low risk" class="passions-image" />
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- How It Works Section -->
    <section id="how_it_works" class="how-it-works-section py-8 py-lg-12">
      <v-container class="pa-0">
        <v-row no-gutters>
          <v-col cols="12" lg="6" class="d-none d-lg-block how-it-works-image" />
          <v-col cols="12" lg="6" class="how-it-works-content pa-6 pa-lg-12">
            <h2 class="section-title text-white font-weight-bold mb-6 mt-0">How degree pathways work</h2>
            <ol class="timeline-list">
              <li v-for="(step, index) in howItWorksSteps" :key="index" class="timeline-item d-flex">
                <div class="timeline-number-wrapper position-relative mr-4">
                  <div class="timeline-number d-flex align-center justify-center">
                    {{ index + 1 }}
                  </div>
                  <div v-if="index < howItWorksSteps.length - 1" class="timeline-line" />
                </div>
                <div class="flex-grow-1">
                  <h3 class="timeline-title text-white font-weight-bold mb-1">
                    {{ step.title }}
                  </h3>
                  <p class="timeline-text mb-4">
                    <span v-if="step.hasSignUpLinks">
                      Sign up to create an ASU account.
                      <a href="https://welcome.ea.asu.edu/" class="link-gold">Sign up today</a> <br /><br />
                      <strong class="text-white">Tip:</strong> Submit your ASU application sooner rather than later to
                      ensure a smooth transition to your chosen ASU degree program.
                      <a href="https://admission.asu.edu/apply" class="link-gold">Apply now</a>
                    </span>
                    <span v-else-if="step.hasLink">
                      Find a <a href="#section_degree_pathways" class="link-gold">degree pathway</a> below that matches
                      your interests and select, 'Register'.
                    </span>
                    <span v-else>{{ step.text }}</span>
                  </p>
                </div>
              </li>
            </ol>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Degree Pathways Cards Section -->
    <section id="section_degree_pathways" class="bg-white pt-8 pb-4 py-lg-12">
      <v-container class="px-4 px-lg-0">
        <!-- Title, Subtitle and Search Box Row -->
        <div class="d-flex flex-column flex-md-row align-start align-md-end justify-space-between mb-6 gap-3">
          <!-- Left: Title and Subtitle -->
          <div class="pathway-header-left">
            <h2 class="section-title-dark d-inline-block px-1 mb-2 text-white font-weight-bold">
              Degree pathway options
            </h2>
            <p class="mb-0 mt-3 text-dark">
              Select a pathway interest area to learn more and browse related degree options.
            </p>
          </div>

          <!-- Right: Search Box (50% width on desktop, aligned to bottom) -->
          <div class="pathway-search-wrapper">
            <input v-model="searchQuery" type="text" class="pathway-search-input" placeholder="Search pathways" />
            <svg
              class="pathway-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </div>
        </div>

        <div class="pathway-grid">
          <router-link
            v-for="pathway in filteredPathways"
            :key="pathway.id"
            :to="pathway.link"
            class="pathway-card position-relative d-block"
            @click="handlePathwayCardClick(pathway)"
          >
            <img
              v-if="pathway.image"
              :src="pathway.image"
              :alt="pathway.title + ' background'"
              class="pathway-card-image"
            />
            <div v-else class="pathway-card-placeholder" />
            <div class="pathway-card-overlay d-flex align-end pa-4">
              <div class="pathway-card-content d-flex align-center justify-space-between w-100">
                <h3 class="pathway-card-title text-white font-weight-bold">
                  {{ pathway.title }}
                </h3>
                <img :src="arrowRightIcon" alt="" class="pathway-card-arrow" />
              </div>
            </div>
          </router-link>
        </div>
      </v-container>
    </section>
  </div>
</template>

<script>
import { defineComponent, getCurrentInstance, computed, nextTick, ref } from 'vue';
import arrowRightIcon from '@/assets/icons/arrow-right.svg';
import passionImage from '@/assets/Pathways/degree-pathways-passion.svg';
import pathwaysData from '@/data/pathways.json';
import { DataLayerMixin } from '@/mixins/dataLayerMixin';
import { SessionInitializer } from '@/mixins/sessionInitializer';
import { usePathwayCatalog } from '@/composables/usePathwayCatalog';

export default defineComponent({
  name: 'PathwaysPage',

  mixins: [DataLayerMixin, SessionInitializer],

  created() {
    // Initialize session to get learner data, then check partner access
    this.init('pathways', () => {
      this.checkAndRedirectIfUnauthorized();
    });
  },

  methods: {
    async checkAndRedirectIfUnauthorized() {
      const isPathwaysAllowed = this.$conn?.learnerData?._isPathwaysAllowed;
      // If pathways is not allowed for this user, redirect to dashboard
      if (isPathwaysAllowed === false) {
        this.$router.replace({ name: 'dashboard' });
        return;
      }

      // Fetch the catalog to determine which categories to show
      await this.fetchCatalogData();

      // Handle scroll to degrees section if requested via query param
      if (this.$route.query.scrollTo === 'degrees') {
        nextTick(() => {
          const element = document.getElementById('section_degree_pathways');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        });
      }
    }
  },

  setup() {
    const instance = getCurrentInstance();

    // Use the pathway catalog composable
    const { pathways: catalogPathways, fetchCatalog } = usePathwayCatalog();

    // Track if catalog has been fetched
    const catalogFetched = ref(false);

    // Fetch catalog data
    const fetchCatalogData = async () => {
      try {
        await fetchCatalog();
        catalogFetched.value = true;
      } catch (error) {
        // If catalog fetch fails, show all categories as fallback
        catalogFetched.value = true;
      }
    };

    // Get unique interest areas from catalog
    const availableInterestAreas = computed(() => {
      if (!catalogPathways.value.length) return new Set();
      return new Set(catalogPathways.value.map((p) => p.interestArea).filter(Boolean));
    });

    // Filter pathways based on what's available in the catalog for this partner
    const pathways = computed(() => {
      const allCategories = pathwaysData.categories.map((category) => ({
        id: category.id,
        title: category.title,
        image: category.image,
        link: category.link,
        apiInterestArea: category.apiInterestArea
      }));

      // If catalog hasn't been fetched yet or has no pathways, show all categories
      if (!catalogFetched.value || !catalogPathways.value.length) {
        return allCategories;
      }

      // Filter to only show categories that have pathways in the catalog
      return allCategories.filter((category) => availableInterestAreas.value.has(category.apiInterestArea));
    });

    // Search functionality
    const searchQuery = ref('');

    const filteredPathways = computed(() => {
      if (!searchQuery.value) {
        return pathways.value;
      }

      const query = searchQuery.value.toLowerCase();

      // Search through all pathway degree names in the catalog
      const matchingInterestAreas = new Set();

      catalogPathways.value.forEach((pathway) => {
        const pathwayName = (pathway.pathwayName || '').toLowerCase();
        if (pathwayName.includes(query)) {
          matchingInterestAreas.add(pathway.interestArea);
        }
      });

      // Filter category cards to only show those that have matching pathways
      return pathways.value.filter((pathway) => matchingInterestAreas.has(pathway.apiInterestArea));
    });

    const howItWorksSteps = [
      {
        title: 'Register for a pathway',
        text: "Find a degree pathway below that matches your interests and select, 'Register'."
      },
      {
        title: 'Enroll in courses',
        text: 'Enroll in courses that align with your pathway.'
      },
      {
        title: 'Track your progress',
        text: "When you're happy with your final grade, add your courses to your ASU transcript and track your progress towards pathway completion."
      },
      {
        title: 'Earn admission',
        text: 'If you have met all requirements for your pathway and have submitted an application, you will be admitted to your degree program.'
      }
    ];

    const scrollToPathways = () => {
      const element = document.getElementById('section_degree_pathways');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    };

    // Track pathway card click
    const handlePathwayCardClick = (pathway) => {
      // GA tracking for pathway card click with dynamic text
      if (instance?.proxy?.asuoDataLayerClick) {
        instance.proxy.asuoDataLayerClick('PathwaysPage', '0', { text: pathway.title });
      }
    };

    return {
      pathways,
      filteredPathways,
      searchQuery,
      howItWorksSteps,
      scrollToPathways,
      arrowRightIcon,
      passionImage,
      handlePathwayCardClick,
      fetchCatalogData
    };
  }
});
</script>

<style scoped>
.pathways-page {
  font-family: Arial, Helvetica, sans-serif;
}

/* Hero Section */
.hero-section {
  min-height: 200px;
  height: 25vh;
  max-height: 280px;
  background-image: url('../assets/Pathways/degree-pathways-hero.jpg');
  background-size: cover;
  background-position: center top;
}

.hero-overlay {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0.7) 100%);
}

.hero-content {
  padding-bottom: 1.5rem;
}

.hero-title {
  font-size: 1.75rem;
  line-height: 1.1;
}

.hero-subtitle {
  font-size: 1.5rem;
  color: #ffc627;
}

/* Section Titles */
.section-title {
  font-size: 1.75rem;
  color: #191919;
}

.section-title-dark {
  font-size: 1.75rem;
  background-color: #333333;
}

/* Body Text */
.body-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #191919;
}

.text-dark {
  color: #191919;
}

/* Links */
.link-maroon {
  color: #8c1d40;
  text-decoration: underline;
}

.link-gold {
  color: #ffc627;
  text-decoration: underline;
}

/* Passions Image */
.passions-image {
  max-width: 100%;
  height: auto;
}

/* Explore Button */
.explore-btn {
  background-color: #ffc627 !important;
  color: #191919 !important;
  font-weight: 700;
  text-transform: none;
  border-radius: 50px;
}

/* How It Works Section */
.how-it-works-section {
  background-image: url('../assets/Pathways/topo-pattern.jpg');
  background-size: auto;
  background-position: center;
  background-repeat: repeat;
}

.how-it-works-image {
  background-image: url('../assets/Pathways/degree-pathways-how-it-works.jpg');
  background-size: cover;
  background-position: center;
  min-height: 500px;
}

.how-it-works-content {
  background-color: #191919;
}

/* Timeline */
.timeline-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.timeline-item {
  padding-bottom: 0;
}

.timeline-number-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.timeline-number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid #ffc627;
  background-color: #191919;
  color: white;
  flex-shrink: 0;
}

.timeline-line {
  width: 2px;
  flex-grow: 1;
  background-color: #ffc627;
  min-height: 20px;
}

.timeline-title {
  font-size: 1.125rem;
}

.timeline-text {
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #bfbfbf;
}

/* Pathway Header Layout */
.pathway-header-left {
  flex: 1;
}

@media (min-width: 960px) {
  .pathway-header-left {
    flex: 0 0 48%;
  }
}

/* Pathway Search Box */
.pathway-search-wrapper {
  position: relative;
  width: 100%;
}

@media (min-width: 960px) {
  .pathway-search-wrapper {
    width: 50%;
    flex: 0 0 50%;
  }
}

.pathway-search-input {
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  background-color: white;
  outline: none;
}

.pathway-search-input:focus {
  border-color: #191919;
}

.pathway-search-input::placeholder {
  color: #747474;
}

.pathway-search-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #747474;
  pointer-events: none;
}

.gap-3 {
  gap: 1rem;
}

/* Pathway Cards */
.pathway-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

@media (min-width: 600px) {
  .pathway-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1264px) {
  .pathway-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.pathway-card {
  height: 200px;
  overflow: hidden;
  border-radius: 12px;
  text-decoration: none;
}

@media (min-width: 600px) {
  .pathway-card {
    height: auto;
    aspect-ratio: 308 / 420;
  }
}

.pathway-card:hover {
  opacity: 0.95;
}

.pathway-card-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.pathway-card-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #4a4a4a 0%, #2d2d2d 100%);
  border-radius: 12px;
}

.pathway-card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 20%, rgba(0, 0, 0, 0.85) 100%);
  border-radius: 12px;
}

.pathway-card-title {
  font-size: 24px;
  line-height: 28px;
  margin: 0;
}

.pathway-card-content {
  gap: 12px;
}

.pathway-card-arrow {
  flex-shrink: 0;
}

/* Tablet and up (768px+) */
@media (min-width: 768px) {
  .hero-section {
    min-height: 300px;
    height: 35vh;
    max-height: 400px;
  }

  .hero-content {
    padding-bottom: 2rem;
  }

  .hero-title {
    font-size: 2.5rem;
  }
}

/* Desktop (960px+) */
@media (min-width: 960px) {
  .hero-section {
    min-height: 350px;
    height: 50vh;
    max-height: 500px;
  }

  .hero-content {
    padding-bottom: 1.5rem;
  }

  .hero-title {
    font-size: 3.75rem;
  }

  .hero-subtitle {
    font-size: 1.75rem;
    width: 75%;
  }

  .section-title {
    font-size: 2.25rem;
  }

  .section-title-dark {
    font-size: 2.25rem;
  }
}
</style>
